import { useEffect, useState } from 'react';
import '../../styles/onboardingFlow/signUp/signUp.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import store from '../../store/store';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { requestUserSignUp } from '../../lib/api/sign-up';
import { loadSignUpMerchant } from '../../lib/api/signup-merchant';
import FooterLinks from '../../components/footer/FooterLinks';
import useTitle from '../../customHooks/useTitle';
import AINormalInput from '../../ui/inputs/AINormalInput';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { crossSVG, tickSVG } from '../../lib/allCustomSVGs';
import { clearUserSlice, setReferral } from '../../store/reducers/userSlice';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { useGoogleLogin } from '@react-oauth/google';
import { asyncHandler } from '../../lib/api/async-handler';
import { submitLogToBugsnag } from '../../lib/api/log';
import { LOGIN_API } from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { setIsInitSuccess, setMessage } from '../../store/reducers/appSlice';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import {
  setAccessToken,
  setAgencyAccessToken,
  setEmployeeIdentity,
} from '../../store/reducers/authSlice';
import moment from 'moment';
import { fetchAppInit } from '../../lib/api/app-init';
import googleButtonImg from '../../assets/googleIcon.png';
import { AnimatePresence, motion } from 'framer-motion';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import MobileLogoCard from '../mobileLogoCard/MobileLogoCard';
import {
  acceptEmployeeInvitation,
  fetchIdentity,
} from '../../lib/api/identity/identity-api';
import { HOME_ROUTE } from '../../lib/constants/route';
import { initialSignUpEmail, prefillEmailFromSearch } from './prefill-email';

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  useTitle(t('Register'));
  const params = new URLSearchParams(window.location.search);

  // Phase 11-Bridge — when `/register?invite=TOKEN` is hit, the form switches
  // into "accept employee invitation" mode: hide the seller-only fields, swap
  // the submit handler for the agency-portal accept-endpoint call, and route
  // the new employee to HOME_ROUTE on success. Without the token, the legacy
  // seller signup flow runs unchanged.
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const isInviteMode = Boolean(inviteToken);

  const authToken = store.getState().auth.authorization.accessToken;
  const referral = useSelector((state: RootState) => state.user?.referral);
  const preUserInvitation = useSelector(
    (state: RootState) => state.user.preUserInvitation
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [isLongEnough, setIsLongEnough] = useState<boolean>(false);

  const validatePassword = (password: string) => {
    setHasUppercase(/[A-Z]/.test(password));
    setHasLowercase(/[a-z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[^a-zA-Z0-9]/.test(password));
    setIsLongEnough(password.length >= 8);
  };

  const showPasswordValidationView =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  // A visitor who is not signed in has no merchant in the store yet, and
  // registration needs its slug. Invitation mode posts elsewhere.
  useEffect(() => {
    if (!isInviteMode) {
      void loadSignUpMerchant();
    }
  }, [isInviteMode]);

  useEffect(() => {
    // Check if the referral exists in the URL
    const referralParam = params.get('referral');
    // The address the link carries (an invitation link names the invited
    // address); the field stays editable.
    const emailParam = prefillEmailFromSearch(window.location.search);

    // If referral exists in URL and it's not already in Redux, update Redux state
    if (referralParam && typeof referralParam === 'string') {
      dispatch(setReferral(referralParam));
    } else {
      dispatch(setReferral(''));
    }

    // If email parameter exists in URL, auto-fill the email input
    if (emailParam) {
      setEmail(emailParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      preUserInvitation.id &&
      preUserInvitation.data?.email &&
      isNotEmptyString(preUserInvitation.data?.email)
    ) {
      // On an invitation link the invited address wins over a remembered one.
      setEmail(
        initialSignUpEmail(
          window.location.search,
          preUserInvitation.data?.email
        )
      );
    }
  }, [preUserInvitation]);

  const isDisabled =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough ||
    email === '' ||
    !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/.test(email);

  async function handleSignUpClick(e: any) {
    setLoading(true);
    e.preventDefault();

    // clear all existing user data before sign up
    dispatch(clearUserSlice());

    const sendingData: { email: string; password: string; [key: string]: any } =
      {
        email: email,
        password: password,
      };

    if (referral && referral !== '') {
      sendingData.is_referred = 1;
      sendingData.referral_code = referral;
    }

    const isRegistrationSuccess = await requestUserSignUp(sendingData);

    if (!isRegistrationSuccess) {
      setLoading(false);
    }
  }

  /**
   * Phase 11-Bridge — accept-invite submit handler.
   *
   * POSTs `{ token, password }` to `/api/v1/agency/employees/accept`.
   * On success the response contains a freshly minted `api_key` (shown to
   * the user only via the Redux-stored token, never displayed), plus the
   * `employee` record. We mirror the agency-portal Login flow by also
   * calling `fetchIdentity()` with the new key so the employeeIdentity
   * slice is fully populated before the user lands on the dashboard.
   */
  async function handleAcceptInviteClick(e: any) {
    e.preventDefault();
    if (!inviteToken) return;
    setLoading(true);
    setInviteError(null);
    try {
      const res = await acceptEmployeeInvitation(inviteToken, password);
      // 1) Persist the freshly minted employee API key.
      store.dispatch(setAgencyAccessToken(res.api_key));
      // 2) Seed the employee identity from the accept response so the
      //    portal has *something* to render against immediately. The shape
      //    differs slightly from AgencyEmployeeIdentity (AgencyEmployee
      //    uses `id`/`email`/`name`/`role`), so we map the common fields.
      store.dispatch(
        setEmployeeIdentity({
          type: 'employee',
          employee_id: res.employee?.id ?? null,
          email: res.employee?.email ?? null,
          name: res.employee?.name ?? null,
          role: res.employee?.role ?? null,
        })
      );
      // 3) Best-effort upgrade to the full identity (accessible shops,
      //    account_type, reconnect surface). If this fails we still have
      //    the bare-bones identity from step 2 and a valid token.
      try {
        const identity = await fetchIdentity(res.api_key);
        store.dispatch(setEmployeeIdentity(identity));
      } catch (identityErr) {
        submitLogToBugsnag(
          'warning',
          `[SignUp invite] fetchIdentity after accept failed: ${identityErr}`
        );
      }
      navigate(HOME_ROUTE, { replace: true });
    } catch (err: any) {
      const status = err?.status as number | undefined;
      let message =
        err?.message ||
        'We could not accept this invite. Please ask your agency owner to resend.';
      if (status === 404 || status === 410) {
        message = 'This invite has expired or has already been used.';
      } else if (status === 400 || status === 422) {
        message = 'Invalid invite token. Please use the link from your email.';
      }
      setInviteError(message);
      submitLogToBugsnag('error', `[SignUp invite] accept failed: ${err}`);
    } finally {
      setLoading(false);
    }
  }

  const normalLoginProcess = (response: any, isWebAuthnLogin?: boolean) => {
    store.dispatch(setIsInitSuccess(false));
    const token_type = 'Bearer';
    const { access_token, agency_api_key } = response?.data;

    if (isNotEmptyString(access_token)) {
      store.dispatch(
        setAccessToken({
          sellerToken: access_token,
          type: token_type,
          expiresOn: moment().add(21, 'days'),
        })
      );
      // Phase 11-Bridge / Phase 12: store the emp_ key minted alongside the JWT
      // and land on the dashboard (legacy FE, now wired with new proposals + stats).
      if (isNotEmptyString(agency_api_key)) {
        store.dispatch(setAgencyAccessToken(agency_api_key));
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }

      //make init call to get all info about logged-In user
      fetchAppInit();
    } else {
      console.log('Access Token is empty');
      submitLogToBugsnag('error', `Access Token is empty`);
      store.dispatch(
        setMessage({
          message: t('UnauthorizedAccess'),
          messageType: 'error',
        })
      );
    }
  };

  const googleSignIn = async (googleAccessToken: any) => {
    try {
      const sendingData: {
        oauth: string;
        role: string;
        token: string;
        [key: string]: any;
      } = {
        oauth: 'google',
        role: 'seller',
        token: googleAccessToken,
        bypass: params.get('bypass') === 'true',
      };

      if (referral && referral !== '') {
        sendingData.is_referred = 1;
        sendingData.referral_code = referral;
      }

      const requestInfo: RequestInfo = {
        url: LOGIN_API,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
        // Store the httpOnly `tara_session` cookie set on the post-signup login
        // so the session is restored on reload (same fix as the login flow).
        credentials: 'include',
      };

      const actionType = 'LOGIN_VERIFICATION';
      const customMsg = t('LoginSuccess-msg');

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        true
      );

      const response = res.data;

      if (res.actionType === actionType && response.success === true) {
        normalLoginProcess(response);
      } else {
        store.dispatch(
          setMessage({
            message: response.message,
            messageType: 'error',
          })
        );
      }
    } catch (exception) {
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  };

  const googleAuthentication = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      googleSignIn(tokenResponse?.access_token);
    },
  });

  const passwordValidationView = () => {
    return (
      <motion.div
        initial={{ opacity: 0.1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0.1 }}
        transition={{ duration: 0.3 }}
        className='onboarding-signup__validation'
      >
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasUppercase ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneUppercaseLetter')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasLowercase ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneLowercaseLetter')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasNumber ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneNumber')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasSpecialChar ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneSpecialCharacter')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {isLongEnough ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeast8Characters')}</p>
        </div>
      </motion.div>
    );
  };

  if (!authToken.sellerToken) {
    return (
      <div className='onboarding-signup'>
        <div className='onboarding-signup__container'>
          {/* Mobile logo card (slideshow, mobile only) */}
          <MobileLogoCard />

          {/* Logo (desktop only; hidden on mobile when card is shown) */}
          <div className='onboarding-signup__logo'>
            <img src={adupLogoSet} alt='AdUp Logo' />
          </div>

          {/* Heading */}
          <div className='onboarding-signup__header'>
            <h1>
              {isInviteMode ? 'Accept your invitation' : t('SignUp-new-ui')}
            </h1>
            <p>
              {isInviteMode
                ? 'Set a password to finish joining your team.'
                : t('SignUpSubText-new-ui')}
            </p>
          </div>

          {/* Phase 11-Bridge — invite banner. There is no GET preview
              endpoint yet, so we render a generic prompt rather than the
              inviting org's name. */}
          {isInviteMode && (
            <div
              className='onboarding-signup__field'
              style={{
                padding: '1rem 1.2rem',
                marginBottom: '1.2rem',
                background: '#f4f6ff',
                border: '1px solid #c8d0f0',
                borderRadius: '0.6rem',
                color: '#1f2a55',
                fontSize: '1.3rem',
                lineHeight: 1.4,
              }}
            >
              You've been invited to join an AdUp agency workspace. Confirm your
              email and choose a password to accept the invite.
            </div>
          )}

          {/* Inline error for an expired / invalid invite. */}
          {isInviteMode && inviteError && (
            <div
              className='onboarding-signup__field'
              style={{
                padding: '1rem 1.2rem',
                marginBottom: '1.2rem',
                background: '#fdecec',
                border: '1px solid #f3b3b3',
                borderRadius: '0.6rem',
                color: '#7a1f1f',
                fontSize: '1.3rem',
                lineHeight: 1.4,
              }}
            >
              {inviteError}{' '}
              <span
                className='onboarding-signup__signin-link'
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/login')}
              >
                Go to sign in
              </span>
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={
              isInviteMode ? handleAcceptInviteClick : handleSignUpClick
            }
            className='onboarding-signup__form'
          >
            {/* Email Input */}
            <div className='onboarding-signup__field'>
              <AINormalInput
                id='sign-up-form-email'
                value={email}
                type='email'
                changeListeners={[(e) => setEmail(e.target.value)]}
                placeholder={t('EmailAddress')}
                innerPlaceholder={'you@example.com'}
                required={true}
                pattern='^(([^<>\(\)\[\]\\.,;:\s@"]+(\.[^<>\(\)\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
              />
            </div>

            {/* Password Input */}
            <div className='onboarding-signup__field onboarding-signup__field--password'>
              <AINormalInput
                id='sign-up-form-password'
                value={password}
                changeListeners={[(e) => handleChange(e)]}
                placeholder={t('Password')}
                type='password'
                innerPlaceholder={t('ChooseAPassword')}
                required={true}
                focusListeners={[
                  (e) => {
                    setIsPasswordInputFocused(true);
                  },
                ]}
                blurListeners={[
                  (e) => {
                    setIsPasswordInputFocused(false);
                  },
                ]}
              />
              {/* Password Validation - Floating */}
              <AnimatePresence exitBeforeEnter>
                {isPasswordInputFocused &&
                  showPasswordValidationView &&
                  passwordValidationView()}
              </AnimatePresence>
            </div>

            {/* Continue Button */}
            <div className='onboarding-signup__button'>
              <MainSquareButton
                value={isInviteMode ? 'Accept invitation' : t('Continue')}
                type='submit'
                loading={loading}
                disabled={isDisabled}
                classes='onboarding-signup__continue-btn'
              />
            </div>

            {/* Phase 11-Bridge — Google OAuth + seller "OR" separator are
                seller-signup specific. An invited employee already has an
                identity on the agency side; OAuth here would mint a new
                seller account by mistake. Hide both in invite mode. */}
            {!isInviteMode && (
              <>
                {/* OR Separator */}
                <div className='onboarding-signup__separator'>
                  <span>{t('OR')}</span>
                </div>

                {/* Google Button */}
                <div className='onboarding-signup__button'>
                  <MainSquareButton
                    value={t('ContinueWithGoogle')}
                    image={googleButtonImg}
                    type='button'
                    onClick={() => {
                      googleAuthentication();
                    }}
                    classes='onboarding-signup__google-btn'
                  />
                </div>
              </>
            )}

            {/* Sign In Link */}
            <div className='onboarding-signup__signin'>
              <span>
                {t('AlreadyHaveAnAccount')}{' '}
                <span
                  className='onboarding-signup__signin-link'
                  onClick={() => navigate('/login')}
                >
                  {t('SignIn')}
                </span>
              </span>
            </div>

            {/* Terms and Privacy */}
            <div className='onboarding-signup__terms'>
              {/* <span>{t('ByContinuingYouAgree')} </span> */}
              <FooterLinks isRegisterPage={true} />
            </div>
          </form>
        </div>

        {/* Copyright */}
        <div className='onboarding-signup__copyright'>
          <span>© AdUp {new Date().getFullYear()}</span>
        </div>
      </div>
    );
  } else {
    window.location.assign('/');
    return null;
  }
};

export default SignUp;
